import { MutationCache, QueryCache, QueryClient } from 'react-query';

import { logWarn } from '@24i/nxg-core-utils/src/logger';
import { QUERY_KEYS } from '@24i/nxg-sdk-photon';

import type { FullCfg, TierName } from './types';

export const DEFAULT_STALE_TIME = 60 * 10 * 1000; // 10 minutes
export const DEFAULT_CACHE_TIME = 3600000; // 1 hour

function setGlobalDefaults(queryClient: QueryClient, config: FullCfg) {
    const staleTime = config.allStale ? 0 : config.staleTimeMs;
    const cacheTime = config.cacheTimeMs;
    queryClient.setDefaultOptions({
        queries: {
            staleTime,
            cacheTime,
            refetchOnWindowFocus: config.refetch.onFocus,
            refetchOnReconnect: config.refetch.onReconnect,
            refetchOnMount: config.refetch.onMount,
        },
    });
}

function setTierDefaults(queryClient: QueryClient, config: FullCfg) {
    // Use tier refetch policies if present, else fallback to global config
    const tierOpts = (tierName: TierName) => {
        const tier = config.tiers[tierName];
        const global = config.refetch;
        return {
            staleTime: tier.staleTimeMs,
            cacheTime: tier.cacheTimeMs,
            refetchOnMount:
                tier.refetchOnMount !== undefined ? tier.refetchOnMount : global.onMount,
            refetchOnWindowFocus:
                tier.refetchOnWindowFocus !== undefined
                    ? tier.refetchOnWindowFocus
                    : global.onFocus,
            refetchOnReconnect:
                tier.refetchOnReconnect !== undefined
                    ? tier.refetchOnReconnect
                    : global.onReconnect,
        };
    };
    type QueryKey = string | string[];
    const apply = (prefix: QueryKey, opts: Record<string, unknown>) =>
        queryClient.setQueryDefaults(prefix as string[], opts);
    // Precedence: React Query applies the default of the longest matching prefix
    (Object.keys(config.keyGroups) as TierName[]).forEach((tier) => {
        config.keyGroups[tier].forEach((k) => apply(Array.isArray(k) ? k : [k], tierOpts(tier)));
    });
}

export const DEFAULT_RQ_CONFIG: FullCfg = {
    version: '1.0.0',
    enabled: true,
    allStale: false,
    staleTimeMs: DEFAULT_STALE_TIME, // 10 minutes
    cacheTimeMs: DEFAULT_CACHE_TIME, // 1 hour
    refetch: { onFocus: false, onReconnect: true, onMount: true },
    tiers: {
        absoluteZero: {
            staleTimeMs: 0,
            cacheTimeMs: 0,
            refetchOnWindowFocus: true, // Critical data: check on tab focus
        },
        zero: {
            staleTimeMs: 0,
            cacheTimeMs: 30 * 60 * 1000, // 30 minutes
            // Uses global defaults: refetchOnMount=true, refetchOnReconnect=true
        },
        short: {
            staleTimeMs: 5 * 60 * 1000, // 5 minutes
            cacheTimeMs: 60 * 60 * 1000, // 1 hour
            // Uses global defaults: refetchOnMount=true, refetchOnReconnect=true
        },
        medium: {
            staleTimeMs: 15 * 60 * 1000, // 15 minutes
            cacheTimeMs: 2 * 60 * 60 * 1000, // 2 hours
            refetchOnMount: false, // Less aggressive: only on reconnect
        },
        long: {
            staleTimeMs: 60 * 60 * 1000, // 1 hour
            cacheTimeMs: 4 * 60 * 60 * 1000, // 4 hours
            refetchOnMount: false,
            refetchOnReconnect: false, // Very conservative: rely on cache
        },
    },
    keyGroups: {
        absoluteZero: [
            QUERY_KEYS.mySubscriptions,
            QUERY_KEYS.entitledSubscriptions,
            QUERY_KEYS.pinValidation,
        ],
        zero: [
            QUERY_KEYS.epg,
            QUERY_KEYS.search,
            QUERY_KEYS.editions,
            QUERY_KEYS.blockers,
            QUERY_KEYS.page,
            QUERY_KEYS.availableSubscriptions,
        ],
        short: [
            QUERY_KEYS.config,
            QUERY_KEYS.continueWatchingPlaylist,
            QUERY_KEYS.lastWatchPlaylist,
            QUERY_KEYS.myFavoritesPlaylist,
            QUERY_KEYS.myFavorites,
            [QUERY_KEYS.playlists, QUERY_KEYS.liveNow],
            QUERY_KEYS.isPurchased,
            QUERY_KEYS.recordings,
        ],
        medium: [
            QUERY_KEYS.media,
            QUERY_KEYS.deeplink,
            QUERY_KEYS.user,
            QUERY_KEYS.profiles,
            QUERY_KEYS.storageInfo,
            QUERY_KEYS.assets,
            [QUERY_KEYS.assets, QUERY_KEYS.assetDetail],
            [QUERY_KEYS.assets, QUERY_KEYS.assetExtras],
            [QUERY_KEYS.assets, QUERY_KEYS.assetRelated],
            [QUERY_KEYS.assets, QUERY_KEYS.assetSeasons],
            [QUERY_KEYS.assets, QUERY_KEYS.assetRating],
            QUERY_KEYS.playlists,
            [QUERY_KEYS.playlists, QUERY_KEYS.mostPopular],
        ],
        long: [
            [QUERY_KEYS.media, QUERY_KEYS.channels],
            QUERY_KEYS.appTheme,
            QUERY_KEYS.appAllThemes,
            QUERY_KEYS.welcomeScreens,
            QUERY_KEYS.termsAndConditions,
            QUERY_KEYS.navigationConfig,
            QUERY_KEYS.menu,
        ],
    },
    persist: {
        enabled: true,
        mode: 'blacklist',
        blacklistKeys: [
            QUERY_KEYS.mySubscriptions,
            QUERY_KEYS.availableSubscriptions,
            QUERY_KEYS.entitledSubscriptions,
            QUERY_KEYS.pinValidation,
            QUERY_KEYS.search,
        ],
        whitelistKeys: [],
        maxAgeMs: 60 * 60 * 1000, // 1 hour
        throttleTimeMs: 1500, // 1.5 seconds
        buster: 'v1',
    },
};

export function createQueryClient(config: FullCfg = DEFAULT_RQ_CONFIG) {
    const queryClient = new QueryClient({
        queryCache: new QueryCache({
            onError: (error, query) => {
                logWarn('Query error', query?.queryKey, error);
            },
        }),
        mutationCache: new MutationCache({
            onError: (error, variables, context, mutation) => {
                logWarn(
                    'Mutation error',
                    mutation?.options?.mutationKey,
                    error,
                    variables,
                    context
                );
            },
        }),
    });

    // Handle enabled flag logic
    // false: disables all caching and persistence
    // true/missing: normal config
    if (config.enabled === false) {
        queryClient.setDefaultOptions({
            queries: {
                staleTime: 0,
                cacheTime: 0,
                refetchOnWindowFocus: true,
                refetchOnReconnect: true,
                refetchOnMount: true,
            },
        });
        // Note: Persistence is disabled via useRemoteConfiguration when enabled is false
        return queryClient;
    }

    // enabled is not false (i.e., true, undefined, or any value except false) - use normal config
    setGlobalDefaults(queryClient, config);
    setTierDefaults(queryClient, config);
    return queryClient;
}

// Lazy-initialized singleton - first call determines config
let queryClientSingleton: QueryClient | null = null;
export const getQueryClient = (config?: FullCfg) => {
    if (!queryClientSingleton) {
        queryClientSingleton = createQueryClient(config);
    }
    return queryClientSingleton;
};
